import ResizableBox from '@/components/ResizableBox';
import { useEventEmitter, useRequest } from 'ahooks';
import { message } from 'antd';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import type { CatalogTreeAction } from './CatalogTree';
import CatalogTree, { type SelectData } from './CatalogTree';
import VehicleInfo from './VehicleInfo';
import { useAutoHeight } from 'xui-pro';
import { getManualTree } from '@/api/sip/bookmap/manualTree';
import type { ManualRecord } from '@/api/sip/bookmap/list';
import DitaDetail from './DitaDetail';
import CircuitDiagramDetail from './CircuitDiagram';
import Empty from './Empty';
import type { EventEmitter } from 'ahooks/lib/useEventEmitter';
import api from '@/api';
import type { ManualTreeRecord } from '@/api/sip/bookmap/manualTree';
import { shallowEqual, useSelector } from 'react-redux';
import type { RootState } from '@/models/store';
import { useTranslation } from '@/hooks/useTranslation';

import styles from './index.module.less';

type TabContentProps = {
  record: ManualRecord;
  modelCodeName?: string;
  seriesCodeName?: string;
  defaultSelected?: SelectData;
  searchSelect$?: EventEmitter<SelectData>;
  tabChange$?: EventEmitter<void>;
  active?: boolean;
};

const TabContent = (props: TabContentProps) => {
  const $ = useTranslation();
  const region = useSelector<RootState, string>(
    state => state.LocaleModel.countryCode,
    shallowEqual
  );
  const { record } = props;
  const containerRef = useRef<HTMLDivElement>(null);
  const { modelCode, vin } = useParams<any>();
  const [currentSelect, setCurrentSelect] = useState<SelectData | undefined>();

  const defaultSelected = useMemo(() => {
    if (props.defaultSelected && props.defaultSelected.bookMapCode === record.bookmapCode) {
      return props.defaultSelected;
    }
  }, [props.defaultSelected, record.bookmapCode]);

  const { _height } = useAutoHeight(containerRef, true, undefined, 395, false, 10);

  const catalogTreeRef = useRef<CatalogTreeAction>(null);
  props.searchSelect$?.useSubscription((data: SelectData) => {
    if (data.bookMapCode === props.record.bookmapCode) {
      catalogTreeRef.current?.select([data.uniqueCode]);
    }
  });

  /**
   * @desc 获取手册参数列表
   */
  const { run: getCircuitParams, data: paramMaps } = useRequest(
    async () => {
      let res: any = {};
      const params = {
        bookmapCode: record.bookmapCode,
        bookmapVersion: record.bookmapVersion
      };
      // 如果是预览状态，在手册层级才需要获取参数
      res = await api.sip.bookmap.getParamMap(params);
      return res.data || {};
    },
    {
      manual: true
    }
  );

  /**
   * @desc 加载目录树
   */
  const { data, loading } = useRequest(
    async () => {
      let res: any = {};
      if (!vin && !modelCode) return;
      res = await getManualTree({
        bookmapCode: props.record.bookmapCode,
        bookmapVersion: props.record.bookmapVersion,
        modelCode,
        region
      });
      if (res.code !== 200) message.error(res.msg);
      return (res.data ? [res.data] : []) as ManualTreeRecord[];
    },
    { manual: false }
  );

  useEffect(() => {
    if (record.bookmapType === 'DT') {
      getCircuitParams();
    }
  }, [record.bookmapType, getCircuitParams]);

  const [showCatalog, setShowCatalog] = useState(true);
  const handlerPull = useCallback(() => {
    setShowCatalog(false);
  }, []);
  const handlePush = useCallback(() => {
    setShowCatalog(true);
  }, []);

  const resizeStatusChange$ = useEventEmitter<boolean>();

  return (
    <>
      <div ref={containerRef}>
        {_height &&
          (data ? (
            <div style={{ height: _height }} className={`${styles['main']} ${styles['container']}`}>
              <ResizableBox
                direction='right'
                width={!showCatalog ? 0 : 240}
                minWidth={!showCatalog ? 0 : 160}
                maxWidth={!showCatalog ? 0 : 340}
                style={{ height: '100%', overflow: 'hidden' }}
                disabled={!showCatalog}
                onResizeStart={() => {
                  resizeStatusChange$.emit(true);
                }}
                onResizeStop={() => {
                  resizeStatusChange$.emit(false);
                }}
              >
                {showCatalog ? (
                  <></>
                ) : (
                  <div className={`${styles['pull-push-box']} ${styles['pull-box']}`}>
                    <div className={styles['push-icon']} onClick={handlePush} />
                  </div>
                )}
                <div
                  className={styles['left-content']}
                  style={{ display: showCatalog ? 'flex' : 'none' }}
                >
                  <VehicleInfo
                    vin={vin}
                    modelCodeName={props.modelCodeName}
                    seriesCodeName={props.seriesCodeName}
                  />
                  <CatalogTree
                    onSelect={setCurrentSelect}
                    defaultSelected={defaultSelected}
                    select={currentSelect}
                    data={data}
                    ref={catalogTreeRef}
                    bookMapCode={props.record.bookmapCode}
                  />
                </div>
                {showCatalog ? (
                  <div className={`${styles['pull-push-box']} ${styles['push-box']}`}>
                    <div className={styles['pull-icon']} onClick={handlerPull} />
                  </div>
                ) : (
                  <></>
                )}
              </ResizableBox>
              {!currentSelect ? <Empty /> : <></>}
              <div className={styles['right-content']}>
                {currentSelect?.code && record.bookmapType !== 'DT' && (
                  <DitaDetail
                    code={currentSelect.code}
                    version={currentSelect.version}
                    title={currentSelect.name}
                    key={currentSelect.uniqueCode}
                    uniqueCode={currentSelect.uniqueCode}
                    active={props.active || false}
                    tabChange$={props.tabChange$}
                    manualRecord={props.record}
                    resizeStatusChange$={resizeStatusChange$}
                  />
                )}
                {currentSelect?.code && currentSelect.version && record.bookmapType === 'DT' && (
                  <CircuitDiagramDetail
                    topicCode={currentSelect.code}
                    topicVersion={currentSelect.version}
                    bookmapCode={record.bookmapCode}
                    bookmapVersion={record.bookmapVersion}
                    defaultPCode=''
                    key={currentSelect.uniqueCode}
                    paramMaps={paramMaps}
                  />
                )}
              </div>
            </div>
          ) : (
            <div className={`${styles['main']} ${styles['container']}`} style={{ height: _height }}>
              <Empty description={loading ? $('加载中...') : $('暂无数据')} />
            </div>
          ))}
      </div>
    </>
  );
};
export default TabContent;
